import React, { useState, useEffect, useMemo } from "react";
import ReactDom from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Badge from "../../badge/badge";
import Button from "../../button/button";
import Popover from "../../popover/popover";
import ZeroData from "../../zeroData/zeroData";
import styles from "./importDataTable.module.css";
import { downloadFile, updateFileStatus, updateFileVisibility, getImportMapping } from "../../../services/import";
import DownloadIcon from "../../../assets/icons/download.svg";
import EyeOffIcon from "../../../assets/icons/eye-off.svg";
import ManageColumnsIcon from "../../../assets/icons/manage-columns-action.svg";
import ViewDataIcon from "../../../assets/icons/document-arrow-right.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import { useImportFlow } from "../../../context/ImportFlowContext";
import CircleXMarkIcon from "../../../assets/icons/circle-xmark.svg";
import CircleXMarkRedIcon from "../../../assets/icons/circle-xmark-red.svg";
import CircleCorrectIcon from "../../../assets/icons/circle-correct.svg";
import loadingGif from "../../../assets/loading.gif";

const ITEMS_PER_PAGE = 15

const SMART_IMPORT_CATEGORY = "Importação Inteligente"

// Categorias cujos dados têm tela de dados consolidados. Adicionar aqui conforme as demais telas
// passarem a ler o parâmetro fileId da URL. (Hoje só a acadêmica está ligada no backend.)
const CONSOLIDATED_ROUTE_BY_CATEGORY = {
  "Acadêmico": "/app/dados-consolidados-academicos"
}

// Arquivo importado que não alimentou nenhum indicador: fica guardado com as linhas brutas.
const NO_INDICATORS_STATUS = "SemIndicadores"
const RAW_DATA_ROUTE = "/app/dados-brutos-importados"

const FILTER_ALL = "all"
const STATUS_ATIVO = "ativo"
const STATUS_INATIVO = "inativo"
const VIS_VISIBLE = "visible"
const VIS_HIDDEN = "hidden"
const VIS_ALL = "all"
const USER_EMPTY = "__sem_nome__"

const ImportDataTable = ({ files = [], onRefresh }) => {
  const { institution } = useInstitution();
  const { setImportData } = useImportFlow();
  const navigate = useNavigate();

  const [deletePopover, setDeletePopover] = useState("");
  const [fileList, setFileList] = useState(files)
  const [loadingFile, setLoadingFile] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [hideConfirmFile, setHideConfirmFile] = useState(null)
  const [hidingFile, setHidingFile] = useState(false)

  const [filterImportedBy, setFilterImportedBy] = useState(FILTER_ALL)
  const [filterStatus, setFilterStatus] = useState(FILTER_ALL)
  const [filterVisibility, setFilterVisibility] = useState(VIS_VISIBLE)

  useEffect(() => {
    setFileList(files)
    setCurrentPage(1)
  }, [files])

  useEffect(() => {
    setCurrentPage(1)
  }, [filterImportedBy, filterStatus, filterVisibility])

  const { importerNames, hasUnnamedImporter } = useMemo(() => {
    const names = new Set()
    let unnamed = false
    fileList.forEach((f) => {
      const n = f.importedByUserName?.trim()
      if (n) names.add(n)
      else unnamed = true
    })
    return {
      importerNames: [...names].sort((a, b) => a.localeCompare(b, "pt-BR")),
      hasUnnamedImporter: unnamed
    }
  }, [fileList])

  const filteredFiles = useMemo(() => {
    return fileList.filter((f) => {
      if (filterImportedBy !== FILTER_ALL) {
        if (filterImportedBy === USER_EMPTY) {
          if (f.importedByUserName?.trim()) return false
        } else if ((f.importedByUserName || "") !== filterImportedBy) {
          return false
        }
      }
      if (filterStatus === STATUS_ATIVO && f.status !== "Ativo") return false
      if (filterStatus === STATUS_INATIVO && f.status !== "Inativo") return false
      if (filterVisibility === VIS_VISIBLE && f.isVisible === false) return false
      if (filterVisibility === VIS_HIDDEN && f.isVisible !== false) return false
      return true
    })
  }, [fileList, filterImportedBy, filterStatus, filterVisibility])

  const totalPages = Math.ceil(filteredFiles.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const paginatedFiles = filteredFiles.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  const hasActiveFilters =
    filterImportedBy !== FILTER_ALL ||
    filterStatus !== FILTER_ALL ||
    filterVisibility !== VIS_VISIBLE

  const clearFilters = () => {
    setFilterImportedBy(FILTER_ALL)
    setFilterStatus(FILTER_ALL)
    setFilterVisibility(VIS_VISIBLE)
  }

  const handleConfirmHideFile = async () => {
    if (!hideConfirmFile) return;
    try {
      setHidingFile(true);
      await updateFileVisibility(hideConfirmFile.correlationId, institution?.id);
      setFileList(prevFiles =>
        prevFiles.map(item =>
          item.correlationId === hideConfirmFile.correlationId
            ? { ...item, isVisible: false }
            : item
        )
      )
      toast.success("Arquivo removido da visualização com sucesso.");
      setHideConfirmFile(null);
    } catch (error) {
      toast.error("Erro ao ocultar o arquivo. Tente novamente.");
    } finally {
      setHidingFile(false);
    }
  }

  const formatDate = (date) => {
    const dateObject = new Date(date);
    const options = { year: "numeric", month: "numeric", day: "numeric" };

    return dateObject.toLocaleDateString("pt-BR", options);
  };

  const handleDownload = async (file) => {
    try {
      const response = await downloadFile(file.correlationId, institution?.id);

      if (!response) {
        toast.error("Não foi possível baixar o arquivo. O arquivo pode não estar mais disponível para download.");
        return;
      }

      generateFile(response, file.fileName);
    } catch (error) {
      toast.error("Erro ao baixar o arquivo. Tente novamente.");
    }
  };

  // Abre a tela de revisão em modo somente-leitura com o mapeamento gravado nesta importação.
  const handleViewMapping = async (file) => {
    try {
      const snapshot = await getImportMapping(file.correlationId, institution?.id);
      setImportData({
        smart: true,
        readOnly: true,
        fileName: file.fileName,
        headerSpec: snapshot?.headerSpec ?? null,
        detection: {
          detectedTypes: snapshot?.detectedTypes || [],
          receivedHeaders: snapshot?.receivedHeaders || [],
          sampleRows: snapshot?.sampleRows || [],
          unmappedColumns: snapshot?.unmappedColumns || [],
          rowCount: snapshot?.rowCount,
          notice: snapshot?.notice,
          source: snapshot?.source || "ai"
        }
      });
      navigate("/app/importar-dados/revisao-inteligente");
    } catch (error) {
      const msg = typeof error === "string" ? error : "Mapeamento não disponível para esta importação.";
      toast.error(msg);
    }
  };

  // Abre a tela de dados consolidados já recortada por este arquivo (via fileId na URL). Arquivo
  // guardado sem indicador não tem consolidado: vai para a tela das linhas brutas.
  const handleViewData = (file) => {
    const route = file?.processingStatus === NO_INDICATORS_STATUS
      ? RAW_DATA_ROUTE
      : CONSOLIDATED_ROUTE_BY_CATEGORY[file.category];
    if (!route) {
      return;
    }
    const params = new URLSearchParams({ fileId: file.correlationId });
    if (file.fileName) {
      params.set("fileName", file.fileName);
    }
    navigate(`${route}?${params.toString()}`);
  };

  const handleUpdateFileStatus = async (file) => {
    try {
      setLoadingFile(file.correlationId)
      await updateFileStatus(file.correlationId, institution?.id);
      setDeletePopover("")
      // Atualiza a tela com o retorno da API: recarrega a listagem do servidor (fonte da verdade),
      // em vez de só alternar o status localmente.
      if (onRefresh) {
        await onRefresh()
      } else {
        setFileList(prevFiles =>
          prevFiles.map(item =>
            item.correlationId === file.correlationId
              ? { ...item, status: item.status === "Ativo" ? "Inativo" : "Ativo" }
              : item
          )
        )
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFile("")
    }
  };

  const generateFile = (data, fileName) => {
    const href = URL.createObjectURL(data);

    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeaderRow}>
        <h2>Arquivos Enviados</h2>
        {hasActiveFilters && (
          <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
            Limpar filtros
          </button>
        )}
      </div>

      <div className={styles.filtersBar}>
        <div className={styles.filterField}>
          <label htmlFor="filter-imported-by">Importado por</label>
          <select
            id="filter-imported-by"
            className={styles.filterSelect}
            value={filterImportedBy}
            onChange={(e) => setFilterImportedBy(e.target.value)}
          >
            <option value={FILTER_ALL}>Todos</option>
            {hasUnnamedImporter && (
              <option value={USER_EMPTY}>Não informado</option>
            )}
            {importerNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filterField}>
          <label htmlFor="filter-status">Status do arquivo</label>
          <select
            id="filter-status"
            className={styles.filterSelect}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value={FILTER_ALL}>Todos</option>
            <option value={STATUS_ATIVO}>Ativos</option>
            <option value={STATUS_INATIVO}>Inativos</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <label htmlFor="filter-visibility">Visibilidade</label>
          <select
            id="filter-visibility"
            className={styles.filterSelect}
            value={filterVisibility}
            onChange={(e) => setFilterVisibility(e.target.value)}
          >
            <option value={VIS_VISIBLE}>Visíveis na lista</option>
            <option value={VIS_HIDDEN}>Ocultos</option>
            <option value={VIS_ALL}>Todos</option>
          </select>
        </div>
      </div>

      {filteredFiles.length > 0 ? (
        <>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nome do Arquivo</th>
              <th>Data de Envio</th>
              <th>Importado por</th>
              <th>Dados</th>
              <th>Categoria</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            {paginatedFiles.map((item, index) => (
              <tr key={index}>
                <td>{item.fileName}</td>
                <td>{formatDate(item.dispatchDate)}</td>
                <td>{item.importedByUserName || "—"}</td>
                <td>{item.qtdDataSent}</td>
                <td>
                  <Badge text={item.category} color="blue" />
                </td>

                <td>
                  {item?.processingStatus === "Processando" ? (
                    <span className={styles.processingStatus}>
                      <img src={loadingGif} alt="Processando" className={styles.processingGif} />
                      Processando...
                    </span>
                  ) : item?.processingStatus === "Erro" ? (
                    <Badge text="Erro no Processamento" color="red" />
                  ) : item?.processingStatus === NO_INDICATORS_STATUS ? (
                    <Badge text="Sem indicadores" color="yellow" />
                  ) : (
                    <Badge
                      text={item?.status === "Ativo" ? "Ativo" : "Inativo"}
                      color={item?.status === "Ativo" ? "green" : "red"}
                    />
                  )}
                </td>

                <td className={styles.actions}>
                  {item?.processingStatus === "Processando" ? (
                    <span style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>Aguardando...</span>
                  ) : (
                  <>
                  <img
                    src={DownloadIcon}
                    alt="Baixar"
                    onClick={() => handleDownload(item)}
                  />

                  {(item?.processingStatus === NO_INDICATORS_STATUS
                    || (CONSOLIDATED_ROUTE_BY_CATEGORY[item.category] && item.status === "Ativo")) && (
                    <img
                      src={ViewDataIcon}
                      alt="Ver dados importados"
                      title="Ver dados importados"
                      onClick={() => handleViewData(item)}
                    />
                  )}

                  {item.category === SMART_IMPORT_CATEGORY && (
                    <img
                      src={ManageColumnsIcon}
                      alt="Ver mapeamento"
                      title="Ver mapeamento"
                      onClick={() => handleViewMapping(item)}
                    />
                  )}

                  <img
                    src={EyeOffIcon}
                    alt="Ocultar"
                    title="Ocultar da visualização"
                    onClick={() => setHideConfirmFile(item)}
                  />

                  <div className={styles.deletePopoverContainer}>
                    <img
                      src={item.status === "Ativo" ? CircleXMarkIcon : CircleCorrectIcon}
                      alt={item.status === "Ativo" ? "Inativar" : "Ativar"}
                      onClick={() => setDeletePopover(item.correlationId)}
                    />

                    {deletePopover === item.correlationId && (
                      <Popover
                        onClose={() => setDeletePopover("")}
                        content={
                          <div className={styles.deletePopover}>
                            <h6>
                              {item.status === "Ativo"
                                ? "Você realmente deseja inativar os dados deste arquivo?"
                                : "Você realmente deseja ativar os dados deste arquivo?"}
                            </h6>
                            {loadingFile === item.correlationId ? (
                              <Button
                                text="Carregando..."
                                variation="secondary"
                                size="big"
                              />
                            ) : (
                              <Button
                                text={item.status === "Ativo" ? "Inativar arquivo" : "Ativar arquivo"}
                                variation="secondary"
                                size="big"
                                onClick={() => handleUpdateFileStatus(item)}
                              />
                            )}
                          </div>
                        }
                      />
                    )}
                  </div>
                  </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹ Anterior
            </button>

            <div className={styles.paginationPages}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`${styles.paginationPage} ${page === currentPage ? styles.paginationPageActive : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Próximo ›
            </button>
          </div>
        )}
        </>
      ) : fileList.length > 0 ? (
        <ZeroData>
          <h4>
            Nenhum arquivo corresponde aos filtros selecionados.{" "}
            <span>Ajuste os filtros ou limpe-os para ver mais resultados.</span>
          </h4>
        </ZeroData>
      ) : (
        <ZeroData>
          <h4>
            Você ainda não enviou nenhum arquivo.{" "}
            <span>Comece enviando um arquivo para importar os seus dados.</span>
          </h4>
        </ZeroData>
      )}
      {hideConfirmFile && ReactDom.createPortal(
        <div className={styles.modalBackdrop} onClick={() => !hidingFile && setHideConfirmFile(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Excluir arquivo da visualização</h3>
            <p className={styles.modalText}>
              Você tem certeza que deseja excluir o arquivo <strong>{hideConfirmFile.fileName}</strong> da visualização?
            </p>
            <div className={styles.modalWarning}>
              <img src={CircleXMarkRedIcon} alt="Aviso" className={styles.modalWarningIcon} />
              <span>Essa ação só poderá ser desfeita através do suporte.</span>
            </div>
            <div className={styles.modalActions}>
              <Button
                text="Cancelar"
                variation="primary"
                size="big"
                onClick={() => setHideConfirmFile(null)}
                disabled={hidingFile}
              />
              <Button
                text={hidingFile ? "Excluindo..." : "Confirmar exclusão"}
                variation="danger"
                size="big"
                onClick={handleConfirmHideFile}
                disabled={hidingFile}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ImportDataTable;
